import { useRef, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import {
  CameraFront,
  CameraRear,
  Close,
  Delete,
} from "@mui/icons-material";
import Webcam from "react-webcam";

interface EntregaCameraModalProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (images: string[]) => Promise<void> | void;
}

export const EntregaCameraModal: React.FC<EntregaCameraModalProps> = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [isFrontFacing, setIsFrontFacing] = useState(false);
  const [images, setImages] = useState<string[]>([]);

  const videoConstraints = {
    facingMode: isFrontFacing ? "user" : "environment",
  };

  const capture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;
    setImages((prev) => [...prev, imageSrc]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleClose = () => {
    if (loading) return;
    setImages([]);
    onClose();
  };

  const handleConfirm = async () => {
    await onConfirm(images);
    setImages([]);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 3,
          width: "90%",
          maxWidth: 900,
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 2,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            backgroundColor: "red",
            color: "white",
            ":hover": {
              bgcolor: "#5c5c5c",
            },
          }}
        >
          <Close />
        </IconButton>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Fotos do Veículo
        </Typography>

        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/png"
          videoConstraints={videoConstraints}
          style={{ width: "100%", height: "auto", borderRadius: 8 }}
        />

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              fullWidth
              onClick={capture}
              disabled={loading}
              sx={{
                backgroundColor: "#303030",
                ":hover": { bgcolor: "#5c5c5c" },
              }}
            >
              Capturar Imagem
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setIsFrontFacing((prev) => !prev)}
              disabled={loading}
              sx={{
                backgroundColor: "#303030",
                ":hover": { bgcolor: "#5c5c5c" },
              }}
              startIcon={isFrontFacing ? <CameraFront /> : <CameraRear />}
            >
              Alternar Câmera
            </Button>
          </Grid>
        </Grid>

        {images.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Fotos capturadas: {images.length}
            </Typography>
            <Grid container spacing={1}>
              {images.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} key={`${index}-${image.slice(0, 20)}`}>
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={image}
                      alt={`Foto ${index + 1}`}
                      style={{
                        width: "100%",
                        borderRadius: 8,
                        display: "block",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeImage(index)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "white",
                        ":hover": {
                          backgroundColor: "rgba(0,0,0,0.8)",
                        },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 3, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || images.length === 0}
            sx={{
              backgroundColor: "#303030",
              ":hover": { bgcolor: "#5c5c5c" },
            }}
          >
            {loading ? "Enviando..." : "Confirmar"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
